import {
    Component,
    Injectable,
    inject
} from "@angular/core";

import {
    MAT_DIALOG_DATA,
    MatDialog,
    MatDialogModule,
    MatDialogRef
} from "@angular/material/dialog";

import { lastValueFrom } from "rxjs";

import { 
    BaseDialogService,
    NonNullableConfigData,
    TilbyDialogContentComponent,
    TilbyDialogToolbarComponent
} from "@tilby/tilby-ui-lib/components/tilby-dialog";

import {
    ConfigurationManagerService,
    EntityDictionariesStoreService
} from "src/app/core";

import { util } from "app/ajs-upgraded-providers";

import {
    Items,
    ItemsCombinations
} from "tilby-models";

import {
    CommonModule
} from "@angular/common";

import {
    MatRadioButton,
    MatRadioGroup
} from "@angular/material/radio";

import {
    TilbyCurrencyPipe
} from "@tilby/tilby-ui-lib/pipes/tilby-currency";

import {
    FormsModule
} from "@angular/forms";

import {
    TranslateModule
} from "@ngx-translate/core";

export type SelectSaleItemCombinationDialogData = { item: Items };

type CombinationsForm = {
    combinationRef: ItemsCombinations,
    name: string,
    price: number
}

@Component({
    selector: 'app-select-sale-item-combination',
    templateUrl: './select-sale-item-combination.component.html',
    styleUrls: ['./select-sale-item-combination.component.scss'],
    standalone: true,
    imports: [
        CommonModule,
        TilbyDialogContentComponent,
        TilbyDialogToolbarComponent,
        MatDialogModule,
        MatRadioGroup,
        MatRadioButton,
        TilbyCurrencyPipe,
        FormsModule,
        TranslateModule
    ]
})
export class SelectSaleItemCombinationComponent {
    private readonly configurationManagerService = inject(ConfigurationManagerService);
    private readonly entityDictionaries = inject(EntityDictionariesStoreService);
    private readonly matDialogRef = inject(MatDialogRef);
    private readonly utilService = inject(util);
    protected readonly data: SelectSaleItemCombinationDialogData = inject(MAT_DIALOG_DATA);
    
    protected showPrices = this.configurationManagerService.isUserPermitted('show_prices_ui');

    protected item = this.data.item;
    protected selectedCombination?: ItemsCombinations;
    protected priceList = this.utilService.getCurrentPriceList();
    protected combinations: CombinationsForm[] = [];

    ngOnInit(){
        this.combinations = (this.item.combinations || []).map((combination) => {
            return {
                combinationRef: combination,
                name: (combination.combination_values || []).map((value) => value.variation_value).join(', '),
                price: combination[`price${this.priceList}` as (`price${number}` & keyof ItemsCombinations)] ?? combination[`price1`]
            };
        });
    }

    getCombinationStockAvailability(combination: any) {
        if (!!this.configurationManagerService.getPreference('cashregister.show_stock_on_showcase') && this.item.stock_type === 'simple') {
            switch (this.entityDictionaries.getStock(`combination_id_${combination.combinationRef.id}`)?.available) {
                case 'available': case 'producible':
                    return 'stock-info-available';
                case 'unavailable':
                    return 'stock-info-unavailable';
                case 'alert':
                    return 'stock-info-alert';
                default:
                    return 'stock-info-nostock';
            }
        }
    };

    protected confirm() {
        this.matDialogRef.close(this.selectedCombination);
    }
}

@Injectable({
    providedIn: "root",
})
export class SelectSaleItemCombinationDialogService extends BaseDialogService {
    private readonly dialogRef = inject(MatDialog);

    public openDialog(config: NonNullableConfigData<SelectSaleItemCombinationDialogData>): Promise<ItemsCombinations | undefined> {
        const data: SelectSaleItemCombinationDialogData = config.data;

        const configEdited: NonNullableConfigData<SelectSaleItemCombinationDialogData> = {
            ...config,
            ...this.switchMobileDesktopDimensions({ width: '600px' }),
            disableClose: true,
            data,
        };

        return lastValueFrom(
            this.dialogRef.open(SelectSaleItemCombinationComponent, configEdited).afterClosed()
        );
    }
}
